import { ApiClient } from '../../../api/ApiClient';
import { postFileViaLink } from '../../../api/ApiEndpoints';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { ImageInfo } from '../../../concepts/resources/ImageInfo';
import { Camera } from '../../canvas/Camera';
import { Physicals } from '../../canvas/entities/Physicals';
import { imagePopup } from '../../canvas/properties/ImagePopup';
import { PopupManager } from '../../popups/PopupManager';
import { Graphic } from './Graphic';
import { clampDimensions } from './GraphicUtil';

export const createGraphicFromUrl = defineCommand(
    'createGraphicFromUrl',
    'Create: Graphic From Url...',
    async (r, [url, x, y]: [string, number?, number?]) => {

        const camera = r.call(Camera);
        const objects = r.call(ObjectManager);

        const info = await ImageInfo.load(url);
        objects.update(u => {
            const [w, h] = clampDimensions(camera, [info.width, info.height]);
            const inst = Physicals.create(r, Graphic, [x ?? 'auto', y ?? 'auto', w, h], g => {
                g.container = false;
                g.url = info.url;
            });
            u.set('key', `insertGraphic:${inst.id}`);
        });
    },
    [
        stringStep('uri', 'Enter the URI of the image:')
    ]
);

export const createGraphicFromPopup = defineCommand(
    'createGraphicFromPopup',
    'Create: Graphic From Popup...',
    async (r) => {

        const api = r.call(ApiClient);
        const camera = r.call(Camera);
        const objects = r.call(ObjectManager);
        const popups = r.call(PopupManager);
        const result = await popups.invoke(imagePopup());

        if (!result.actioned) return;

        // If image policy is host, we need to get the api to download the image to a storage location and provide us a URL, which could
        // take a few moments, so we should create an inital placeholder
        if (result.data.policy === 'host') {
            // Create the placeholder
            const graphic = objects.update(u => {
                const [w, h] = !!result.data.width ? clampDimensions(camera, [result.data.width, result.data.height]) : [] as number[];
                const inst = Physicals.create(r, Graphic, ['auto', 'auto', w, h]);
                inst.container = false;
                u.set('key', `edit:${inst.id}`);
                return inst;
            });
            // Download the link (api side)
            const { url } = await api.exec(postFileViaLink(result.data.url));
            // Update the graphic with the final url
            objects.update(u => {
                u.set('key', `edit:${graphic.id}`);
                graphic.url = url;
            });
        }
        // If image policy is link, we can just directly insert the graphic with the URL provided
        else /* link */ {
            const info = await ImageInfo.load(result.data.url);
            objects.update(u => {
                const [w, h] = clampDimensions(camera, [info.width, info.height]);
                const inst = Physicals.create(r, Graphic, ['auto', 'auto', w, h], x => {
                    x.url = info.url;
                });
                u.set('key', `edit:${inst.id}`);
            });
        }
    },
);
